import React, { useEffect, useState } from "react";
import { Activity, Plus, Smile, BarChart3, MessageSquare } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, EmptyState } from "../components/ui";

const TYPE_LABEL = { ENPS: "eNPS (0–10)", SCALE_5: "Rating (1–5)", YES_NO: "Yes / No" };
const SCALE = { ENPS: [0,1,2,3,4,5,6,7,8,9,10], SCALE_5: [1,2,3,4,5], YES_NO: [{v:1,l:"Yes"},{v:0,l:"No"}] };

export default function Pulse() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ question: "", type: "SCALE_5" });
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);

  const load = () => api.get("/pulse").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const respond = async (p, score) => {
    const comment = window.prompt("Add an optional comment (or leave blank):") || "";
    try { await api.post(`/pulse/${p.id}/respond`, { score, comment }); load(); }
    catch (e) { window.alert(apiError(e.response?.data?.detail)); }
  };
  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/pulse", form); setOpen(false); setForm({ question: "", type: "SCALE_5" }); load(); }
    catch (err) { window.alert(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const close = async (id) => { if (window.confirm("Close this pulse?")) { await api.post(`/pulse/${id}/close`); load(); } };
  const viewResults = async (id) => { const { data } = await api.get(`/pulse/${id}/results`); setResults(data); };
  if (!rows) return <Loading />;

  const toAnswer = rows.filter((p) => p.status === "ACTIVE" && !p.responded);
  return (
    <div>
      <PageHeader title="Pulse" subtitle="Quick check-ins & employee sentiment">
        {isAdmin && <button data-testid="create-pulse" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> New Pulse</button>}
      </PageHeader>

      {toAnswer.length > 0 && (
        <div className="mb-8">
          <h3 className="mb-3 flex items-center gap-2 font-head text-lg font-bold text-slate-900 dark:text-white"><Smile size={18} className="text-accent" /> Share your pulse</h3>
          <div className="space-y-4">
            {toAnswer.map((p) => (
              <Card key={p.id} className="p-6" data-testid={`pulse-answer-${p.id}`}>
                <p className="mb-4 font-medium text-slate-800 dark:text-slate-100">{p.question}</p>
                <div className="flex flex-wrap gap-2">
                  {p.type === "YES_NO" ? SCALE.YES_NO.map((o) => (
                    <button key={o.v} data-testid={`pulse-opt-${p.id}-${o.v}`} onClick={() => respond(p, o.v)} className="rounded-lg border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-700 hover:border-accent hover:bg-accent/5 dark:border-slate-700 dark:text-slate-200">{o.l}</button>
                  )) : SCALE[p.type].map((s) => (
                    <button key={s} data-testid={`pulse-opt-${p.id}-${s}`} onClick={() => respond(p, s)} className="h-10 w-10 rounded-lg border border-slate-200 text-sm font-bold text-slate-700 hover:border-accent hover:bg-accent/5 dark:border-slate-700 dark:text-slate-200">{s}</button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-400">{TYPE_LABEL[p.type]} · responses are anonymous</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      <h3 className="mb-3 font-head text-lg font-bold text-slate-900 dark:text-white">{isAdmin ? "All Pulses" : "History"}</h3>
      {rows.length === 0 ? <EmptyState icon={Activity} title="No pulses yet" hint={isAdmin ? "Create a pulse to gauge sentiment." : "Nothing to answer right now."} /> : (
        <div className="space-y-3" data-testid="pulse-list">
          {rows.map((p) => (
            <Card key={p.id} className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center" data-testid={`pulse-${p.id}`}>
              <div className="flex-1">
                <p className="font-medium text-slate-800 dark:text-slate-100">{p.question}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <Badge tone="info">{TYPE_LABEL[p.type]}</Badge>
                  <Badge value={p.status} />
                  <span>{p.responseCount} response{p.responseCount === 1 ? "" : "s"}</span>
                  {p.responded && <span className="text-emerald-500">✓ you responded</span>}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2">
                  {p.results?.enps !== undefined && <div className="text-right"><p className="text-[10px] uppercase text-slate-400">eNPS</p><p className={`font-head text-xl font-bold ${p.results.enps >= 0 ? "text-emerald-600" : "text-red-600"}`}>{p.results.enps}</p></div>}
                  {p.results && p.results.enps === undefined && <div className="text-right"><p className="text-[10px] uppercase text-slate-400">Avg</p><p className="font-head text-xl font-bold text-accent">{p.results.average}</p></div>}
                  <button data-testid={`pulse-results-${p.id}`} onClick={() => viewResults(p.id)} className="btn-outline px-3 py-1.5 text-sm"><BarChart3 size={13} className="mr-1 inline" />Results</button>
                  {p.status === "ACTIVE" && <button data-testid={`pulse-close-${p.id}`} onClick={() => close(p.id)} className="rounded-md bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">Close</button>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Pulse"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="pulse-form" disabled={busy} className="btn-accent">Launch</button></>}>
        <form id="pulse-form" onSubmit={create} className="space-y-4">
          <div><label className="label">Question *</label><textarea required data-testid="pulse-q" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className="input h-20 py-2" placeholder="e.g. How are you feeling about your workload this week?" /></div>
          <div><label className="label">Type</label>
            <select data-testid="pulse-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">
              {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </form>
      </Modal>

      <Modal open={!!results} onClose={() => setResults(null)} title="Pulse Results"
        footer={<button onClick={() => setResults(null)} className="btn-accent">Close</button>}>
        {results && <div className="space-y-4">
          <p className="font-medium text-slate-800 dark:text-slate-100">{results.question}</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50"><p className="text-[10px] uppercase text-slate-400">Responses</p><p className="font-head text-xl font-bold text-slate-800 dark:text-white">{results.responses}</p></div>
            <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50"><p className="text-[10px] uppercase text-slate-400">Average</p><p className="font-head text-xl font-bold text-accent">{results.average}</p></div>
            {results.enps !== undefined && <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50"><p className="text-[10px] uppercase text-slate-400">eNPS</p><p className={`font-head text-xl font-bold ${results.enps >= 0 ? "text-emerald-600" : "text-red-600"}`}>{results.enps}</p></div>}
          </div>
          {results.enps !== undefined && <div className="flex gap-2 text-center text-xs">
            <div className="flex-1 rounded bg-emerald-50 p-2 dark:bg-emerald-500/10"><b className="text-emerald-600">{results.promoters}</b><br/>Promoters</div>
            <div className="flex-1 rounded bg-slate-50 p-2 dark:bg-slate-800/50"><b>{results.passives}</b><br/>Passives</div>
            <div className="flex-1 rounded bg-red-50 p-2 dark:bg-red-500/10"><b className="text-red-600">{results.detractors}</b><br/>Detractors</div>
          </div>}
          {results.comments?.length > 0 && <div>
            <p className="mb-2 flex items-center gap-1 text-xs font-bold uppercase text-slate-400"><MessageSquare size={12} /> Comments</p>
            <div className="max-h-48 space-y-2 overflow-y-auto">{results.comments.map((c, i) => <p key={i} className="rounded-lg bg-slate-50 p-2.5 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">"{c}"</p>)}</div>
          </div>}
        </div>}
      </Modal>
    </div>
  );
}
